import { useEffect, useState, useCallback, forwardRef } from 'react';
import type { PropsWithoutRef, TransitionEvent } from 'react';
import styled, { css } from 'styled-components';

import { defaultThemeProp } from '../../theme';
import { reflow, withTestIds } from '../../utils';
import type {
  PropsWithDefaults,
  ExcludeStrict,
  WithAttributes,
  RefElement,
  TestIdProp,
  OmitStrict
} from '../../types';
import Configuration from '../Configuration';
import Flex from '../Flex';
import type { FlexProps } from '../Flex';
import { useConsolidatedRef, useElement, useTestIds } from '../../hooks';

import { getBackdropTestIds } from './Backdrop.test-ids';

export type BackdropProps = WithAttributes<
  'div',
  TestIdProp & {
    /**
     * Opacity of the Backdrop.
     * @default 0.4
     */
    alpha?: number;
    /** Props passed to Flex container. */
    container?: ExcludeStrict<FlexProps['container'], true>;
    /**
     * Toggles visibility of Backdrop.
     * @default false
     */
    open?: boolean;
    /**
     * Determines the color of the Backdrop.
     * @default 'dark'
     */
    variant?: 'dark' | 'light';
    /**
     * Transition speed of Backdrop animation.
     * @default 'medium'
     */
    transitionSpeed?: 'slow' | 'medium' | 'fast' | 'none';
    /**
     * The position of the Backdrop.
     * @default 'fixed'
     */
    position?: 'absolute' | 'fixed';
    /** Accepts a callback that gets invoked before opening Backdrop. */
    onBeforeTransitionIn?: () => void;
    /** Accepts a callback that gets invoked after opening Backdrop. */
    onAfterTransitionIn?: () => void;
    /** Accepts a callback that gets invoked before closing Backdrop. */
    onBeforeTransitionOut?: () => void;
    /** Accepts a callback that gets invoked after closing Backdrop */
    onAfterTransitionOut?: () => void;
  }
>;

type BackdropPropsWithDefaults = PropsWithDefaults<
  BackdropProps,
  'open' | 'transitionSpeed' | 'alpha' | 'position'
>;

const transitionSpeedToTheme = {
  slow: 2,
  medium: 1,
  fast: 0.5,
  none: 0
} as const;

export const StyledBackdrop = styled(Flex)<
  OmitStrict<BackdropPropsWithDefaults, 'open'> & { opacity: 0 | 1 }
>(props => {
  const {
    theme: { base }
  } = props;
  const { opacity, alpha, variant, position } = props;
  const transitionDuration = `max(calc(${transitionSpeedToTheme[props.transitionSpeed]} * ${
    base.animation.speed
  }), 1ms)`;

  return css`
    position: ${position};
    z-index: ${position === 'fixed' ? base['z-index'].backdrop : base['z-index'].popover + 1};
    inset-inline: 0;
    inset-block-start: ${position === 'fixed' ? 'var(--appshell-top-offset, 0rem)' : '0'};
    inset-block-end: ${position === 'fixed' ? 'var(--appshell-hard-bottom-offset, 0rem)' : '0'};
    border-radius: inherit;
    opacity: ${opacity};
    transition-property: opacity;
    transition-timing-function: ${props.theme.base.animation.timing.ease};
    /* stylelint-disable declaration-block-no-duplicate-properties */
    transition-duration: 1ms;
    transition-duration: ${transitionDuration};
    /* stylelint-enable declaration-block-no-duplicate-properties */

    ${variant === 'dark' &&
    css`
      background: rgba(0, 0, 0, ${alpha});
    `}

    ${variant === 'light' &&
    css`
      background: rgba(255, 255, 255, ${alpha});
    `}
  `;
});

StyledBackdrop.defaultProps = defaultThemeProp;

type State = 'closed' | 'opening' | 'open' | 'closing';

export default withTestIds(
  forwardRef<RefElement<BackdropProps>, PropsWithoutRef<BackdropProps>>(function Backdrop(
    {
      testId,
      children,
      container,
      open = false,
      variant = 'dark',
      transitionSpeed = 'medium',
      alpha = 0.6,
      position = 'fixed',
      onBeforeTransitionIn,
      onAfterTransitionIn,
      onBeforeTransitionOut,
      onAfterTransitionOut,
      ...restProps
    },
    ref
  ) {
    const testIds = useTestIds(testId, getBackdropTestIds);
    const [state, setState] = useState<State>('closed');
    const [backdropEl, setBackdropEl] = useElement<HTMLDivElement>();
    const backdropRef = useConsolidatedRef(ref, setBackdropEl);

    const onTransitionEnd = useCallback(
      (e: TransitionEvent) => {
        if (e.propertyName !== 'opacity' || e.target !== e.currentTarget) return;
        let nextState: State;

        if (state === 'closing') {
          nextState = 'closed';
          onAfterTransitionOut?.();
        } else {
          nextState = 'open';
          onAfterTransitionIn?.();
        }

        setState(nextState);
      },
      [state, onAfterTransitionOut, onAfterTransitionIn]
    );

    useEffect(() => {
      if (open) {
        if (state === 'closed' || state === 'closing') {
          onBeforeTransitionIn?.();
          reflow();
          setState('opening');
        }
      } else if (state === 'open' || state === 'opening') {
        onBeforeTransitionOut?.();
        setState('closing');
      }
    }, [open]);

    // Using a separate effect instead of combined logic above as it seems more intuitive to mentally manage.
    useEffect(() => {
      if (!backdropRef.current) return;
      const opacity = window.getComputedStyle(backdropRef.current).opacity;
      // If the opacity is already at the destination value, sync the state value.
      if (state === 'closing' && opacity === '0') {
        // Since the transition never ran, we have to pretend it did and call the callback.
        // TODO: This maybe can be fixed with the animation api
        onAfterTransitionOut?.();
        setState('closed');
      }
      if (state === 'opening' && opacity === '1') {
        // ditto, see above...
        onAfterTransitionIn?.();
        setState('open');
      }
    }, [state]);

    return !open && state === 'closed' ? null : (
      <Configuration portalTarget={backdropEl ?? undefined}>
        <StyledBackdrop
          data-testid={testIds.root}
          container={{ justify: 'center', alignItems: 'center', ...container }}
          transitionSpeed={transitionSpeed}
          opacity={state === 'opening' || state === 'open' ? 1 : 0}
          alpha={alpha}
          variant={variant}
          position={position}
          onTransitionEnd={onTransitionEnd}
          ref={backdropRef}
          {...restProps}
        >
          {children}
        </StyledBackdrop>
      </Configuration>
    );
  }),
  getBackdropTestIds
);
